import type { RefinementCtx } from 'zod';

/**
 * Juros e multa de atraso (boleto e Pix com vencimento): tetos, leitura do que
 * foi digitado e o corpo que vai para a API. Mesmas regras de
 * `apps/mobile/src/lib/billetLateCharges.ts`; o servidor valida tudo de novo
 * (`apps/server/src/charges/billet-late-charges.ts`).
 */

export const MAX_PENALTY_PERCENT = 10;
export const MAX_INTEREST_PERCENT = 10;

export const PERCENT_FORMAT_MESSAGE = 'Informe apenas números.';
export const PENALTY_RANGE_MESSAGE = 'A multa vai de 0% a 10%.';
export const INTEREST_RANGE_MESSAGE = 'Os juros vão de 0% a 10% ao mês.';
export const PENALTY_AMOUNT_POSITIVE_MESSAGE = 'Informe uma multa maior que zero.';

/**
 * O limite de 2% do CDC vale para consumidor final, não entre empresas: por
 * isso é aviso, e o teto do sistema é 10%.
 */
export const CDC_PENALTY_NOTICE = 'Venda a consumidor final: o CDC limita a multa a 2%.';

export type PenaltyMode = 'PERCENT' | 'AMOUNT';

/** Padrão da conta, como `GET /customers/features` devolve em `billetLateCharges`. */
export type AccountLateCharges = {
  penaltyPercent: number;
  /** `null` = multa percentual. Ausente em servidor antigo. */
  penaltyAmount?: number | null;
  monthlyInterestPercent: number;
};

/** O pedaço de encargo de um formulário. Vazio = usa o padrão da conta. */
export type LateChargesFormValues = {
  mode: PenaltyMode;
  percent: string;
  amount?: number;
  interest: string;
};

/**
 * Os mesmos campos com os nomes usados nos formulários (iguais aos da API). Os
 * textos são opcionais no tipo porque, sem `strictNullChecks`, o zod infere
 * todo campo como opcional; em runtime o formulário sempre traz string.
 */
export type LateChargesFields = {
  /** Ausente = percentual. */
  latePenaltyMode?: PenaltyMode;
  latePenaltyPercent?: string;
  latePenaltyAmount?: number;
  lateMonthlyInterestPercent?: string;
};

export type LateChargesBody = {
  latePenaltyPercent?: number;
  latePenaltyAmount?: number;
  lateMonthlyInterestPercent?: number;
};

export const LATE_FIELD_PATH = {
  percent: 'latePenaltyPercent',
  amount: 'latePenaltyAmount',
  interest: 'lateMonthlyInterestPercent',
} as const;

// Dígitos com vírgula ou ponto opcional: "2", "1,5", "1.5", ",5".
const PERCENT_PATTERN = /^(\d+[.,]?\d*|[.,]\d+)$/;

/** "1,5" e "1.5" viram 1.5. Vazio vira null (não informado); texto inválido vira NaN. */
export function parsePercent(text: string): number | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  if (!PERCENT_PATTERN.test(trimmed)) return Number.NaN;
  return Number(trimmed.replace(',', '.'));
}

/** Mensagem de erro do campo, ou null quando vale. Vazio vale. */
export function percentError(text: string, max: number, rangeMessage: string): string | null {
  const parsed = parsePercent(text);
  if (parsed === null) return null;
  if (Number.isNaN(parsed)) return PERCENT_FORMAT_MESSAGE;
  if (parsed > max) return rangeMessage;
  return null;
}

export const formatBrl = (value: number): string => `R$ ${value.toFixed(2).replace('.', ',')}`;

const formatPercentText = (value: number): string => String(value).replace('.', ',');

/**
 * Multa fixa: ausente vale (padrão da conta); zero não — para isentar, 0%.
 * `maxAmount` é o valor da cobrança (no carnê, da parcela).
 */
export function penaltyAmountError(amount: number | undefined | null, maxAmount?: number): string | null {
  if (amount === undefined || amount === null) return null;
  if (!Number.isFinite(amount) || amount <= 0) return PENALTY_AMOUNT_POSITIVE_MESSAGE;
  if (maxAmount !== undefined && maxAmount > 0 && amount > maxAmount) {
    return `A multa não pode passar do valor da cobrança (${formatBrl(maxAmount)}).`;
  }
  return null;
}

/** Só o modo escolhido é validado: o campo do outro modo fica escondido. */
export function lateChargesFieldErrors(
  values: LateChargesFormValues,
  maxAmount?: number,
): Partial<Record<keyof typeof LATE_FIELD_PATH, string>> {
  const errors: Partial<Record<keyof typeof LATE_FIELD_PATH, string>> = {};
  if (values.mode === 'AMOUNT') {
    const message = penaltyAmountError(values.amount, maxAmount);
    if (message) errors.amount = message;
  } else {
    const message = percentError(values.percent, MAX_PENALTY_PERCENT, PENALTY_RANGE_MESSAGE);
    if (message) errors.percent = message;
  }
  const interest = percentError(values.interest, MAX_INTEREST_PERCENT, INTEREST_RANGE_MESSAGE);
  if (interest) errors.interest = interest;
  return errors;
}

/** Só o modo escolhido vai: o servidor recusa percentual e valor juntos. */
export function lateChargesBody(values: LateChargesFormValues): LateChargesBody {
  const percent = values.mode === 'PERCENT' ? parsePercent(values.percent) : null;
  const amount = values.mode === 'AMOUNT' ? values.amount : undefined;
  const interest = parsePercent(values.interest);
  return {
    ...(percent !== null ? { latePenaltyPercent: percent } : {}),
    ...(amount !== undefined && amount !== null ? { latePenaltyAmount: amount } : {}),
    ...(interest !== null ? { lateMonthlyInterestPercent: interest } : {}),
  };
}

/** Corpo do `PATCH /customers/billet-late-charges`, que usa outros nomes. */
export function accountLateChargesBody(values: LateChargesFormValues): {
  penaltyPercent?: number;
  penaltyAmount?: number;
  monthlyInterestPercent?: number;
} {
  const body = lateChargesBody(values);
  return {
    ...(body.latePenaltyPercent !== undefined ? { penaltyPercent: body.latePenaltyPercent } : {}),
    ...(body.latePenaltyAmount !== undefined ? { penaltyAmount: body.latePenaltyAmount } : {}),
    ...(body.lateMonthlyInterestPercent !== undefined
      ? { monthlyInterestPercent: body.lateMonthlyInterestPercent }
      : {}),
  };
}

export const lateFormOf = (fields: LateChargesFields): LateChargesFormValues => ({
  mode: fields.latePenaltyMode ?? 'PERCENT',
  percent: fields.latePenaltyPercent,
  amount: fields.latePenaltyAmount,
  interest: fields.lateMonthlyInterestPercent,
});

export const lateFieldsOf = (form: LateChargesFormValues): LateChargesFields => ({
  latePenaltyMode: form.mode,
  latePenaltyPercent: form.percent,
  latePenaltyAmount: form.amount,
  lateMonthlyInterestPercent: form.interest,
});

/** Para o `superRefine` dos schemas zod: cada erro no caminho do seu campo. */
export function addLateChargesIssues(fields: LateChargesFields, ctx: RefinementCtx, maxAmount?: number): void {
  const errors = lateChargesFieldErrors(lateFormOf(fields), maxAmount);
  (Object.keys(errors) as Array<keyof typeof errors>).forEach((key) => {
    ctx.addIssue({ code: 'custom', path: [LATE_FIELD_PATH[key]], message: errors[key] as string });
  });
}

const isFixed = (lc?: AccountLateCharges | null): boolean =>
  lc?.penaltyAmount !== undefined && lc?.penaltyAmount !== null;

/** "Multa 2% · juros 1% ao mês" ou "Multa R$ 10,00 · juros 1% ao mês". */
export function lateChargesSummary(lc: AccountLateCharges): string {
  const penalty = isFixed(lc) ? formatBrl(lc.penaltyAmount as number) : `${formatPercentText(lc.penaltyPercent)}%`;
  return `Multa ${penalty} · juros ${formatPercentText(lc.monthlyInterestPercent)}% ao mês`;
}

/**
 * Placeholders dos campos. Sem padrão carregado, os do sistema (2% e 1%). O
 * campo do modo que a conta não usa fica neutro.
 */
export function lateChargesPlaceholders(lc?: AccountLateCharges | null): {
  percent: string;
  amount: string;
  interest: string;
} {
  const fixed = isFixed(lc);
  return {
    percent: fixed ? `0 a ${MAX_PENALTY_PERCENT}` : formatPercentText(lc?.penaltyPercent ?? 2),
    amount: fixed ? formatBrl(lc?.penaltyAmount as number) : 'Ex.: R$ 10,00',
    interest: formatPercentText(lc?.monthlyInterestPercent ?? 1),
  };
}

/** Formulário da configuração da conta, preenchido com o padrão vigente. */
export function lateChargesFormFrom(lc?: AccountLateCharges | null): LateChargesFormValues {
  if (!lc) return { mode: 'PERCENT', percent: '', amount: undefined, interest: '' };
  const fixed = isFixed(lc);
  return {
    mode: fixed ? 'AMOUNT' : 'PERCENT',
    percent: fixed ? '' : formatPercentText(lc.penaltyPercent),
    amount: fixed ? (lc.penaltyAmount as number) : undefined,
    interest: formatPercentText(lc.monthlyInterestPercent),
  };
}
